import {
  BaseLogo,
  DeliveryVariant,
  ImageRef,
  LogoType,
  LogoVariant,
} from "../../../../core/models/entities/BaseLogo";
import { Design, DesignLogo } from "../../../../core/models/entities/Design";
import { CasingOption } from "../../../shared/functions/casing";

export interface CartItem {
  id: string;
  title: string;
  displayImage: ImageRef;
  type: "Logo" | "Design";
}

// Changed from enum to string type to support any size (XXXS to XXXXL, children sizes like 116, 124, etc.)
export type DesignSize = string;

export interface QuantityRow {
  label: string;
  quantity: number;
}

export interface CartItemQuantity {
  toStorage: number;
  toDelivery: number;
}

export interface CartItemLogo extends CartItem, CartItemQuantity {
  discriminator: LogoType;
  baseLogo: BaseLogo;
  value: string | number | undefined;
  logoVariant: LogoVariant | undefined;
  logoId: number;
  isNumeric: boolean;
  storageQuantities: QuantityRow[];
  deliveryQuantities: QuantityRow[];
  casing?: CasingOption;
}

export interface CartItemDesign extends CartItem {
  design: Design;
  casing?: CasingOption;
  instances: CartItemDesignInstance[];
  customerEntersNamesAndNumbers?: boolean;
  customerEntersNamesAndNumbersEmail?: string;
  excludedDeletedDesignLogos?: DesignLogo[];
}

export interface CartItemDesignInstance extends CartItemQuantity {
  label: string;
  size?: string;
  logos: CartItemDesignInstanceLogo[];
}

export interface CartItemDesignInstanceLogo {
  value: string | number;
  logoVariant: LogoVariant;
  locationIdentifier: string;
  logoId: number;
  deliveryType: DeliveryVariant;
  baseLogo?: BaseLogo;
  designLogoInDesignId?: string;
}

export interface CartSummaryRequest {
  products: CartSummartRequestProduct[];
}
export interface CartSummartRequestProduct {
  productNo: string;
  toStorage: number;
  toDelivery: number;
}

export interface CartSummary {
  prices: CartSummaryPrice[];
  totalQuantity: number;
  totalPrice: number;
  totalUnitPrice: number;
}

export interface CartSummaryPrice {
  productNo: string;
  productName: string;
  productType: string;
  relatedTo: string;
  toStorage: number;
  toDelivery: number;
  price: number;
  unitPrice: number;
  customerPrice?: number;
}

// Currency label for the cart header and the local checkout draft, derived
// from the PRICE ROWS the API returned instead of the viewer's profile.
// The FE never converts amounts — it only sums the rows' adjustedPrice — so
// gluing the profile currency onto that sum invented totals like
// "EUR 67.50" for two rows the API had priced as DKK 33.75 (act-as under an
// EUR vendor, before GetLogo honored actAsOrgId). Each row carries the
// currency MarkupPrices actually stamped, so the rows are the truth.
// Fallbacks: rows without a currency (legacy DKK data) or an empty cart use
// the profile currency, then "DKK" — byte-identical labels for DKK orgs.
// A row/profile mismatch is logged: after the act-as detail fix the two must
// agree, so a warning here means some endpoint priced in the wrong org again.
export function getCartItemsCurrency(
  items: CartItem[],
  profileCurrency?: string,
): string {
  const rowCurrencies = new Set<string>();
  const addCurrency = (currency?: string) => {
    const c = currency?.trim().toUpperCase();
    if (c) rowCurrencies.add(c);
  };
  items.forEach((item) => {
    if (item.type === "Logo") {
      (item as CartItemLogo).baseLogo?.product?.prices?.forEach((p) =>
        addCurrency(p.currency),
      );
    } else if (item.type === "Design") {
      const product = (item as CartItemDesign).design?.product;
      product?.totalPrices?.forEach((p) => addCurrency(p.currency));
      product?.products?.forEach((productRef) =>
        productRef.prices?.forEach((p) => addCurrency(p.currency)),
      );
    }
  });

  return resolveRowCurrency(rowCurrencies, profileCurrency);
}

// Single-row-set variant of getCartItemsCurrency for labels tied to ONE
// product's price rows (the popup's fee breakdown): same normalization,
// same fallbacks, same mismatch warning. Use this instead of the profile
// currency whenever the amount being rendered came out of API price rows.
export function getPriceRowsCurrency(
  prices: Array<{ currency?: string }> | undefined,
  profileCurrency?: string,
): string {
  const rowCurrencies = new Set<string>();
  prices?.forEach((p) => {
    const c = p.currency?.trim().toUpperCase();
    if (c) rowCurrencies.add(c);
  });
  return resolveRowCurrency(rowCurrencies, profileCurrency);
}

function resolveRowCurrency(
  rowCurrencies: Set<string>,
  profileCurrency?: string,
): string {
  const fallback = profileCurrency?.trim().toUpperCase() || "DKK";
  const distinct = Array.from(rowCurrencies);
  if (distinct.length === 1) {
    const rowCurrency = distinct[0];
    if (profileCurrency && rowCurrency !== fallback) {
      console.warn(
        `Cart price rows are in ${rowCurrency} but the profile currency is ${fallback}; labeling with the rows' currency.`,
      );
    }
    return rowCurrency;
  }
  if (distinct.length > 1) {
    console.warn(
      `Cart price rows carry mixed currencies (${distinct.join(
        ", ",
      )}); falling back to the profile currency ${fallback}.`,
    );
  }
  return fallback;
}
